import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ChevronRight, DollarSign, Loader2, Pin } from 'lucide-react';
import { toggleDealFunding } from '../lib/api';
import { toast } from 'sonner';

export const DealTable = ({ deals, compact = false, onDealFunded, pinnedNotes = [], thresholds }) => {
  const navigate = useNavigate();
  const [fundingId, setFundingId] = useState(null);

  const warn = thresholds?.warning || 3;
  const crit = thresholds?.critical || 5;

  // Build a map of deal_id -> pinned notes
  const notesByDeal = {};
  pinnedNotes.forEach(n => {
    if (!notesByDeal[n.deal_id]) notesByDeal[n.deal_id] = [];
    notesByDeal[n.deal_id].push(n);
  });

  const getAgingClass = (days) => {
    if (days >= crit) return 'heat-red';
    if (days >= warn) return 'heat-yellow';
    return 'heat-green';
  };

  const getRiskLabel = (days) => {
    if (days >= crit) return 'red';
    if (days >= warn) return 'yellow';
    return 'green';
  };

  const getRiskClass = (days) => {
    if (days >= crit) return 'status-urgent';
    if (days >= warn) return 'status-warning';
    return 'status-success';
  };

  const getInterviewBadge = (status) => {
    const styles = {
      not_started: 'status-neutral',
      scheduled: 'status-warning',
      completed: 'status-success',
      overdue: 'status-urgent',
    };
    const labels = {
      not_started: 'Not Started',
      scheduled: 'Scheduled',
      completed: 'Completed',
      overdue: 'Overdue',
    };
    return { class: styles[status] || 'status-neutral', label: labels[status] || status };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const handleQuickFund = async (e, dealId, customerName) => {
    e.stopPropagation();
    if (!window.confirm(`Fund deal for ${customerName}?`)) return;
    setFundingId(dealId);
    try {
      await toggleDealFunding(dealId, true);
      toast.success(`${customerName} funded`);
      if (onDealFunded) onDealFunded();
    } catch (err) {
      toast.error('Failed to fund deal');
    } finally {
      setFundingId(null);
    }
  };

  if (!deals || deals.length === 0) {
    return (
      <div className="bg-white border border-slate-200 p-8 text-center">
        <p className="text-slate-500">No deals found</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 overflow-hidden" data-testid="deal-table">
      <div className="overflow-x-auto">
        <table className="w-full data-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Vehicle</th>
              <th>Age</th>
              {!compact && <th>Stips</th>}
              <th>Interview</th>
              {!compact && <th>Manager</th>}
              <th>Risk</th>
              <th>Cash at Risk</th>
              <th>Notes</th>
              <th>Action</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {deals.map((deal) => {
              const interviewBadge = getInterviewBadge(deal.interview_status);
              return (
                <tr
                  key={deal.id}
                  className="deal-row cursor-pointer"
                  onClick={() => navigate(`/deal/${deal.id}`)}
                  data-testid={`deal-row-${deal.id}`}
                >
                  <td>
                    <div>
                      <p className="font-medium text-slate-900">{deal.customer_name}</p>
                      <p className="text-xs text-slate-400 font-mono">#{deal.deal_number}</p>
                    </div>
                  </td>
                  <td>
                    <div>
                      <p className="text-sm">{deal.vehicle_info}</p>
                      <p className="text-xs text-slate-400">{deal.lender}</p>
                    </div>
                  </td>
                  <td>
                    <span className={`inline-flex items-center justify-center w-10 h-10 font-mono font-bold text-sm ${getAgingClass(deal.deal_age)}`}>
                      {deal.deal_age}
                    </span>
                  </td>
                  {!compact && (
                    <td>
                      <span className={`font-mono text-sm ${deal.missing_stips_count > 0 ? 'text-red-600 font-bold' : 'text-emerald-600'}`}>
                        {deal.missing_stips_count}
                      </span>
                    </td>
                  )}
                  <td>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold ${interviewBadge.class}`}>
                      {interviewBadge.label}
                    </span>
                  </td>
                  {!compact && (
                    <td className="text-sm">{deal.finance_manager}</td>
                  )}
                  <td>
                    <span className={`inline-flex px-2 py-1 text-xs font-bold uppercase ${getRiskClass(deal.deal_age)}`}>
                      {getRiskLabel(deal.deal_age)}
                    </span>
                  </td>
                  <td className="font-mono text-sm font-medium">
                    {formatCurrency(deal.pending_funds || deal.amount_financed)}
                  </td>
                  <td className="max-w-[180px]">
                    {notesByDeal[deal.id] ? (() => {
                      const topNote = notesByDeal[deal.id].find(n => n.note_type === 'admin_alert') || notesByDeal[deal.id][0];
                      const isAlert = topNote.note_type === 'admin_alert';
                      return (
                        <div className="relative group" onClick={(e) => e.stopPropagation()}>
                          <div className={`flex items-center gap-1 ${isAlert ? 'text-red-700' : ''}`}>
                            {isAlert ? (
                              <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 animate-pulse" />
                            ) : (
                              <Pin size={12} className="text-yellow-600 fill-current flex-shrink-0" />
                            )}
                            <span className={`text-xs truncate ${isAlert ? 'font-semibold text-red-700' : 'text-slate-600'}`}>
                              {topNote.note_text.slice(0, 30)}{topNote.note_text.length > 30 ? '...' : ''}
                            </span>
                          </div>
                          <div className="absolute z-50 hidden group-hover:block top-full left-0 mt-1 w-72 bg-white border border-slate-200 shadow-lg p-3 text-xs">
                            {notesByDeal[deal.id].map((n, i) => (
                              <div key={n.id} className={`${i > 0 ? 'mt-2 pt-2 border-t border-slate-100' : ''}`}>
                                {n.note_type === 'admin_alert' && (
                                  <span className="text-xs font-bold text-red-700 uppercase mr-1">ADMIN ALERT:</span>
                                )}
                                <p className={`whitespace-pre-wrap ${n.note_type === 'admin_alert' ? 'text-red-800 font-medium' : 'text-slate-800'}`}>{n.note_text}</p>
                                <p className="text-slate-400 mt-1">{n.created_by_name} - {new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })() : (
                      <span className="text-xs text-slate-300">—</span>
                    )}
                  </td>
                  <td>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs font-bold text-emerald-700 border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800"
                      disabled={fundingId === deal.id}
                      onClick={(e) => handleQuickFund(e, deal.id, deal.customer_name)}
                      data-testid={`quick-fund-${deal.id}`}
                    >
                      {fundingId === deal.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <><DollarSign size={14} className="mr-1" /> Fund</>
                      )}
                    </Button>
                  </td>
                  <td>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/deal/${deal.id}`);
                      }}
                      data-testid={`open-deal-${deal.id}`}
                    >
                      <ChevronRight size={16} />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
